import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';
import { getActiveAiRequestConfig } from '../../utils/aiConfig';

const AI_EXPLAIN_SIMULATED_DELAY_MS = 700;

export function useHighlights(documentId) {
  const [highlights, setHighlights] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get(`/api/highlights?document=${encodeURIComponent(documentId)}`)
      .then(setHighlights)
      .catch(() => setError('Unable to load your saved highlights right now.'));
  }, [documentId]);

  const addHighlight = useCallback(
    async (paragraphId, selectedText, startOffset, endOffset) => {
      const created = await api.post('/api/highlights', {
        document: documentId,
        paragraphId,
        selectedText,
        startOffset,
        endOffset,
      });
      setHighlights((prev) => [...prev, created]);
    },
    [documentId]
  );

  const removeHighlight = useCallback(async (id) => {
    await api.delete(`/api/highlights/${id}`);
    setHighlights((prev) => prev.filter((h) => h.id !== id));
  }, []);

  return { highlights, error, addHighlight, removeHighlight };
}

export function useBookmarks(documentId) {
  const [bookmarks, setBookmarks] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get(`/api/bookmarks?document=${encodeURIComponent(documentId)}`)
      .then(setBookmarks)
      .catch(() => setError('Unable to load your saved bookmarks right now.'));
  }, [documentId]);

  const addBookmark = useCallback(
    async (paragraphId) => {
      const created = await api.post('/api/bookmarks', { document: documentId, paragraphId });
      setBookmarks((prev) => [...prev, created]);
    },
    [documentId]
  );

  const removeBookmark = useCallback(async (id) => {
    await api.delete(`/api/bookmarks/${id}`);
    setBookmarks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  return { bookmarks, error, addBookmark, removeBookmark };
}

export function useParagraphExplanations(documentId) {
  const [explanations, setExplanations] = useState({});

  useEffect(() => {
    api
      .get(`/api/paragraph-explanations?document=${encodeURIComponent(documentId)}`)
      .then(setExplanations)
      .catch(() => {});
  }, [documentId]);

  return explanations;
}

function getSelectionOffsets(container) {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;

  const quote = range.toString();
  if (!quote.trim()) return null;

  const preRange = range.cloneRange();
  preRange.selectNodeContents(container);
  preRange.setEnd(range.startContainer, range.startOffset);
  const startOffset = preRange.toString().length;
  const endOffset = startOffset + quote.length;

  return { startOffset, endOffset, quote };
}

function buildSegments(text, highlights) {
  const sorted = [...highlights].sort((a, b) => a.start_offset - b.start_offset);
  const segments = [];
  let cursor = 0;

  sorted.forEach((h) => {
    if (h.start_offset < cursor) return; // skip anything overlapping an already-placed segment
    if (h.start_offset > cursor) {
      segments.push({ key: `plain-${cursor}`, text: text.slice(cursor, h.start_offset), highlight: null });
    }
    segments.push({ key: `hl-${h.id}`, text: text.slice(h.start_offset, h.end_offset), highlight: h });
    cursor = h.end_offset;
  });

  if (cursor < text.length) {
    segments.push({ key: `plain-${cursor}`, text: text.slice(cursor), highlight: null });
  }

  return segments;
}

export function HighlightableParagraph({
  paragraphId,
  text,
  label,
  kind,
  highlights,
  onCreate,
  onDelete,
  isBookmarked,
  onToggleBookmark,
  registerNode,
  paragraphExplanation,
}) {
  const containerRef = useRef(null);
  const rowRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [explainError, setExplainError] = useState('');
  const [aiExplaining, setAiExplaining] = useState(false);
  const [aiRevealed, setAiRevealed] = useState(false);
  const [liveAiExplanation, setLiveAiExplanation] = useState(null);
  const [aiExplainError, setAiExplainError] = useState('');

  const byokConfig = getActiveAiRequestConfig();
  const displayAiExplanation = liveAiExplanation || paragraphExplanation;

  useEffect(() => {
    registerNode?.(paragraphId, rowRef.current);
    return () => registerNode?.(paragraphId, null);
  }, [paragraphId, registerNode]);

  const handleMouseUp = () => {
    if (!containerRef.current) return;
    const offsets = getSelectionOffsets(containerRef.current);
    setPending(offsets);
  };

  const overlapsExisting = pending
    ? highlights.some((h) => pending.startOffset < h.end_offset && pending.endOffset > h.start_offset)
    : false;

  const confirmHighlight = async () => {
    if (!pending || overlapsExisting) return;
    setSaving(true);
    try {
      await onCreate(paragraphId, pending.quote, pending.startOffset, pending.endOffset);
      window.getSelection()?.removeAllRanges();
      setPending(null);
    } catch (err) {
      // leave the pending bar up so the user can retry
    } finally {
      setSaving(false);
    }
  };

  const cancelPending = () => {
    window.getSelection()?.removeAllRanges();
    setPending(null);
  };

  const handleExplain = async () => {
    if (!pending) return;
    const activeConfig = getActiveAiRequestConfig();
    if (!activeConfig) {
      setExplainError('Add your Gemini or Groq API key in Settings to use "Explain with AI".');
      return;
    }
    setExplaining(true);
    setExplainError('');
    try {
      const result = await api.post('/api/ai/explain', {
        provider: activeConfig.provider,
        apiKey: activeConfig.apiKey,
        selectedText: pending.quote,
        context: text,
      });
      setExplanation(result.explanation);
      window.getSelection()?.removeAllRanges();
      setPending(null);
    } catch (err) {
      setExplainError(err.message || 'Unable to generate an explanation right now.');
    } finally {
      setExplaining(false);
    }
  };

  const handleAiExplain = async () => {
    if (aiRevealed) {
      setAiRevealed(false);
      return;
    }
    setAiExplainError('');

    if (byokConfig) {
      setAiExplaining(true);
      try {
        const result = await api.post('/api/ai/explain', {
          provider: byokConfig.provider,
          apiKey: byokConfig.apiKey,
          selectedText: text,
        });
        setLiveAiExplanation(result.explanation);
        setAiRevealed(true);
      } catch (err) {
        setAiExplainError(err.message || 'Unable to generate an explanation right now.');
      } finally {
        setAiExplaining(false);
      }
      return;
    }

    setAiExplaining(true);
    setTimeout(() => {
      setAiExplaining(false);
      setAiRevealed(true);
    }, AI_EXPLAIN_SIMULATED_DELAY_MS);
  };

  const segments = buildSegments(text, highlights);

  return (
    <div className="study-paragraph-row" ref={rowRef} id={`p-${paragraphId}`}>
      <div className="study-paragraph-gutter">
        <button
          type="button"
          className={`study-bookmark-toggle ${isBookmarked ? 'study-bookmark-toggle--active' : ''}`}
          onClick={onToggleBookmark}
          aria-pressed={isBookmarked}
          aria-label={isBookmarked ? 'Remove bookmark from this paragraph' : 'Bookmark this paragraph'}
          title={isBookmarked ? 'Remove bookmark' : 'Bookmark this paragraph — save your place here'}
        />
        {(paragraphExplanation || byokConfig) && (
          <button
            type="button"
            className={`study-ai-explain-toggle ${aiRevealed ? 'study-ai-explain-toggle--active' : ''} ${
              aiExplaining ? 'study-ai-explain-toggle--loading' : ''
            }`}
            onClick={handleAiExplain}
            disabled={aiExplaining}
            aria-pressed={aiRevealed}
            aria-label={aiRevealed ? 'Hide AI explanation' : 'Explain this paragraph with AI'}
            title={aiRevealed ? 'Hide AI explanation' : 'Explain this paragraph with AI'}
          >
            ✦
          </button>
        )}
      </div>
      <div className="study-paragraph-wrap">
        {label && <span className={`study-paragraph-label study-paragraph-label--${kind || 'tip'}`}>{label}</span>}
        <p className="study-paragraph" ref={containerRef} onMouseUp={handleMouseUp}>
          {segments.map((seg) =>
            seg.highlight ? (
              <mark
                key={seg.key}
                className="study-highlight"
                title="Click to remove this highlight"
                onClick={() => onDelete(seg.highlight.id)}
              >
                {seg.text}
              </mark>
            ) : (
              <React.Fragment key={seg.key}>{seg.text}</React.Fragment>
            )
          )}
        </p>
        {aiRevealed && (
          <div className="study-explain-panel">
            <div className="study-explain-panel-header">
              <span>AI explanation</span>
              <button type="button" onClick={() => setAiRevealed(false)} aria-label="Close explanation">
                &times;
              </button>
            </div>
            <p>{displayAiExplanation}</p>
          </div>
        )}

        {aiExplainError && (
          <div className="study-explain-panel study-explain-panel--error">
            <div className="study-explain-panel-header">
              <span>Explain with AI</span>
              <button type="button" onClick={() => setAiExplainError('')} aria-label="Dismiss">
                &times;
              </button>
            </div>
            <p>{aiExplainError}</p>
          </div>
        )}

        {pending && (
          <div className="study-highlight-popover">
            <span className="study-highlight-popover-quote">&ldquo;{truncate(pending.quote)}&rdquo;</span>
            {overlapsExisting ? (
              <span className="study-highlight-popover-warning">Overlaps an existing highlight — remove it first.</span>
            ) : (
              <>
                <button type="button" onClick={confirmHighlight} disabled={saving}>
                  {saving ? 'Saving…' : 'Highlight'}
                </button>
                {byokConfig && (
                  <button type="button" onClick={handleExplain} disabled={explaining}>
                    {explaining ? 'Explaining…' : 'Explain with AI'}
                  </button>
                )}
              </>
            )}
            <button type="button" className="study-highlight-popover-cancel" onClick={cancelPending}>
              Cancel
            </button>
          </div>
        )}

        {explanation && (
          <div className="study-explain-panel">
            <div className="study-explain-panel-header">
              <span>AI explanation</span>
              <button type="button" onClick={() => setExplanation(null)} aria-label="Close explanation">
                &times;
              </button>
            </div>
            <p>{explanation}</p>
          </div>
        )}

        {explainError && (
          <div className="study-explain-panel study-explain-panel--error">
            <div className="study-explain-panel-header">
              <span>Explain with AI</span>
              <button type="button" onClick={() => setExplainError('')} aria-label="Dismiss">
                &times;
              </button>
            </div>
            <p>{explainError}</p>
            {explainError.includes('Settings') && <Link to="/settings/ai">Add your API key &rarr;</Link>}
          </div>
        )}
      </div>
    </div>
  );
}

function truncate(text, max = 80) {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
